import { useState } from 'react';

import { getProductImages } from '@/data/products';
import { cn } from '@/lib/cn';
import type { Product } from '@/types/catalog';

import { ProductPack } from './ProductPack';

interface ProductImageProps {
  product: Product;
  weightLabel?: string;
  className?: string;
  /** Imagens acima da dobra devem carregar de imediato; grids não. */
  priority?: boolean;
  /** Índice da foto na galeria. 0 = principal. */
  index?: number;
  /** Imagem decorativa (o nome do produto já está ao lado, em texto). */
  decorative?: boolean;
}

/**
 * Foto real quando o catálogo tem uma, embalagem desenhada quando não — e a
 * embalagem também é o fallback se a foto falhar ao carregar, então a grade
 * nunca mostra ícone de imagem quebrada.
 */
export function ProductImage({
  product,
  weightLabel,
  className,
  priority,
  index = 0,
  decorative = false,
}: ProductImageProps) {
  const images = getProductImages(product);
  const src = images[index];

  // Chaveado pela URL: sem isso, um card reaproveitado por outro produto
  // herdaria o estado de erro da foto anterior.
  const [failedSrc, setFailedSrc] = useState<string>();
  const showPack = !src || failedSrc === src;

  if (showPack) {
    return (
      <div className={cn('relative overflow-hidden bg-cream-200', className)}>
        <div className="grid h-full w-full place-items-center p-4">
          <ProductPack
            brandId={product.brandId}
            petType={product.petType}
            productName={decorative ? undefined : product.name}
            {...(weightLabel ? { weightLabel } : {})}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={cn('relative overflow-hidden bg-cream-200', className)}>
      <img
        src={src}
        alt={decorative ? '' : (product.imageAlt ?? product.name)}
        loading={priority ? 'eager' : 'lazy'}
        decoding="async"
        {...(priority ? { fetchPriority: 'high' as const } : {})}
        onError={() => setFailedSrc(src)}
        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
      />
    </div>
  );
}
